import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import {
    Identifier,
    useCanAccess,
    useDataProvider,
    useGetOne,
    useNotify,
    useUpdate,
} from 'react-admin';

import type { CrmDataProvider } from '@/providers/types';
import type { Lead, LeadNextStepStatus } from '@/types';
import { leadApiErrorMessage } from './leadApi';
import {
    LeadNextStepsEditor,
    stepsFromRecord,
    type NextStepDraft,
} from './LeadNextStepsEditor';
import { LeadNextStepsStatusList } from './LeadNextStepsStatusList';

const draftsForWrite = (steps: NextStepDraft[]) =>
    steps
        .filter(step => step.name.trim())
        .map(step => ({
            id: step.id,
            name: step.name.trim(),
            notify: step.notify,
            notify_at: step.notify_at,
            status: step.status || 'pending',
        }));

export const LeadListNextStepsDialog = ({
    leadId,
    onClose,
}: {
    leadId: Identifier;
    onClose: () => void;
}) => {
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const { canAccess: canUpdate = false } = useCanAccess({
        resource: 'leads',
        action: 'edit',
    });
    const { data: lead, isPending, refetch } = useGetOne<Lead>('leads', {
        id: leadId,
        meta: { includes: 'lead_next_steps' },
    });
    const [update, { isPending: isSaving }] = useUpdate();
    const [editing, setEditing] = useState(false);
    const [steps, setSteps] = useState<NextStepDraft[]>([]);
    const [statusBusy, setStatusBusy] = useState(false);

    useEffect(() => {
        if (lead) setSteps(stepsFromRecord(lead.next_steps));
    }, [lead]);

    const startEdit = () => {
        if (lead) setSteps(stepsFromRecord(lead.next_steps));
        setEditing(true);
    };

    const cancelEdit = () => {
        if (lead) setSteps(stepsFromRecord(lead.next_steps));
        setEditing(false);
    };

    const handleStatus = async (stepId: number, status: LeadNextStepStatus) => {
        setStatusBusy(true);
        try {
            await dataProvider.patchLeadNextStepStatus(leadId, stepId, status);
            notify('Next step updated', { type: 'info' });
            await refetch();
        } catch (err) {
            notify(leadApiErrorMessage(err, 'Failed to update next step'), {
                type: 'warning',
            });
        } finally {
            setStatusBusy(false);
        }
    };

    const handleSave = () => {
        if (!lead) return;
        update(
            'leads',
            {
                id: lead.id,
                data: {
                    ...lead,
                    next_steps: draftsForWrite(steps),
                },
                previousData: lead,
            },
            {
                onSuccess: () => {
                    notify('Next steps saved', { type: 'info' });
                    setEditing(false);
                    refetch();
                },
                onError: err =>
                    notify(leadApiErrorMessage(err, 'Failed to save next steps'), {
                        type: 'warning',
                    }),
            }
        );
    };

    const busy = isSaving || statusBusy;

    return (
        <Dialog
            open
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            TransitionProps={{ timeout: 0 }}
            sx={{ '& .MuiDialog-paper': { transform: 'none !important' } }}
        >
            <DialogTitle
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}
            >
                <Box flex={1}>
                    {lead?.name?.trim() || 'Unnamed lead'} — next steps
                </Box>
                {canUpdate && !editing && !isPending ? (
                    <Button onClick={startEdit} disabled={busy}>
                        Edit
                    </Button>
                ) : null}
            </DialogTitle>
            <DialogContent>
                {isPending || !lead ? (
                    <CircularProgress size={24} sx={{ my: 2 }} />
                ) : editing ? (
                    <LeadNextStepsEditor value={steps} onChange={setSteps} />
                ) : (
                    <LeadNextStepsStatusList
                        steps={lead.next_steps}
                        onStatus={canUpdate ? handleStatus : undefined}
                        disabled={busy}
                    />
                )}
            </DialogContent>
            <DialogActions>
                {editing ? (
                    <>
                        <Button onClick={cancelEdit} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? <CircularProgress size={20} /> : 'Save'}
                        </Button>
                    </>
                ) : (
                    <Button onClick={onClose} disabled={busy}>
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
